import { useState } from "react";
import { BenefitsEditor } from "@/components/proposal/BenefitsEdit";

const Line = ({ x, testid }) => (
  <li className="flex items-start justify-between gap-4 border-b border-[var(--line)] py-3 text-sm" data-testid={testid}>
    <span>
      {x.name}
      {x.product && <span className="block text-xs text-[var(--stone-dark)]">{x.product.manufacturer} · {x.product.sku}</span>}
      {x.concern && <span className="block text-xs text-[var(--stone-dark)]">{x.concern}</span>}
      {x.safety && (
        <span className="mt-1 block text-xs" data-testid={`${testid}-safety`}>
          <span className="mr-1 rounded-full bg-[var(--terracotta,#C4572E)] px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-white">{x.safety.label}</span>
          {x.safety.note}{" "}
          {x.safety.sources?.map((s, i) => <a key={s.url} href={s.url} target="_blank" rel="noreferrer" className="underline" data-testid={`${testid}-safety-source-${i}`}>{s.label}</a>).reduce((a, b) => [a, " · ", b])}
        </span>
      )}
    </span>
    <span className="shrink-0 font-mono-ui text-[0.65rem] uppercase tracking-[0.08em] text-[var(--stone-dark)]">{x.type === "Service" ? "Quote required" : x.product?.price != null ? `$${x.product.price.toLocaleString()}` : x.type}</span>
  </li>
);

export const Scope = ({ p }) => (
  <section className="grid gap-10 md:grid-cols-2" data-testid="scope-section">
    <div>
      <div className="eyebrow mb-1" data-testid="required-heading">Required maintenance — owner&apos;s responsibility</div>
      <p className="mb-3 text-xs text-[var(--stone-dark)]" data-testid="required-note">{p.requiredNote}</p>
      <ul data-testid="required-list">{p.scope.required.map((x, i) => <Line key={x.id} x={x} testid={`required-item-${i}`} />)}</ul>
      {!p.scope.required.length && <p className="text-sm text-[var(--stone-dark)]">No habitability repairs identified in the photo. A professional inspection may find more.</p>}
    </div>
    <div>
      <div className="eyebrow mb-1">Optional upgrades</div>
      <p className="mb-3 text-xs text-[var(--stone-dark)]">The part community support can go toward.</p>
      <ul data-testid="optional-list">{p.scope.optional.map((x, i) => <Line key={x.id} x={x} testid={`optional-item-${i}`} />)}</ul>
    </div>
  </section>
);

export const Benefits = ({ p, onChange }) => {
  const groups = [["owner", "For the owner"], ["residents", "For residents"], ["contributors", "For contributors"]];
  const [editing, setEditing] = useState(false);
  return (
    <section data-testid="benefits-section">
      <div className="mb-1 flex items-center justify-between gap-3">
        <div className="eyebrow">Benefits</div>
        {(p.isClaimant || p.isProposer) && !editing && <button className="btn-quiet" onClick={() => setEditing(true)} data-testid="benefits-edit-button">{p.isClaimant ? "Edit as owner" : "Edit benefits"}</button>}
      </div>
      <p className="mb-4 text-xs text-[var(--stone-dark)]" data-testid="terms-label">{p.termsLabel} · {p.benefitsSource}</p>
      <div className="grid gap-6 sm:grid-cols-3">
        {groups.map(([k, label]) => (
          <div key={k} data-testid={`benefits-${k}`}>
            <div className="mb-2 text-sm font-medium">{label}</div>
            {(p.benefits[k] || []).map((b, i) => (
              <div key={i} className="mb-2" data-testid={`benefit-${k}-${i}`}>
                {b.type && <span className="mb-1 inline-block rounded-full border border-[var(--line)] px-2 py-0.5 text-[0.65rem]">{b.type}</span>}
                <p className="text-sm text-[var(--stone-dark)]">{b.text}</p>
                <p className="text-[0.65rem] text-[var(--stone-dark)]/80">{b.label} · Proposed terms — not a binding agreement</p>
              </div>
            ))}
            {!p.benefits[k]?.length && <p className="text-sm text-[var(--stone-dark)]/60">—</p>}
          </div>
        ))}
      </div>
      {editing && <BenefitsEditor p={p} onChange={onChange} onClose={() => setEditing(false)} />}
    </section>
  );
};
